// Public legal pages (#18, #2). Plain-language renderings of docs/PRIVACY.md
// and docs/TERMS.md — the docs are the master; keep these in sync.
import { Link } from "react-router-dom";
import { useAuth } from "../auth";

function Chrome({ title, children }: { title: string; children: React.ReactNode }) {
  const { uid, loading } = useAuth();
  return (
    <div className="landing">
      <header className="topbar">
        <span className="brand">VocabKit</span>
        <nav>
          {!loading && uid ? <Link to="/app">My kits</Link> : <Link to="/signin">Sign in</Link>}
        </nav>
      </header>
      <section>
        <h1>{title}</h1>
        {children}
      </section>
      <footer className="footer muted">
        <Link to="/legal/privacy">Privacy</Link> · <Link to="/legal/terms">Terms</Link>
      </footer>
    </div>
  );
}

export function PrivacyPage() {
  return (
    <Chrome title="Privacy">
      <p><strong>Short version:</strong> accounts belong to grown-ups. We collect the
        photos you take of word lists — nothing else, and nothing from your kids beyond
        the name you give a profile.</p>
      <h2>What we collect</h2>
      <ul>
        <li>Your email (that&apos;s the whole account — sign-in links, no passwords).</li>
        <li>Kid profile names and (optional) grade — you type them, you can delete them.</li>
        <li>Photos of word lists. We strip camera data (including GPS) in your browser
          <em> before upload</em>, and they&apos;re auto-deleted within 7 days — sooner once
          your kit is built.</li>
        <li>Your kits and their PDFs, so your library survives the school year.</li>
        <li>Billing status (plan + renewal date). <strong>Card details never touch our
          servers</strong> — payments happen on Stripe&apos;s pages.</li>
      </ul>
      <h2>Who helps us run it</h2>
      <ul>
        <li><strong>Google Cloud</strong> (hosting, database), <strong>Cloudflare</strong>
          (the website), <strong>Stripe</strong> (payments).</li>
        <li><strong>DeepSeek</strong> reads your sanitized photos to turn them into word
          lists. <strong>fal.ai</strong> draws the kit artwork from text prompts only — no
          photos, no names.</li>
        <li>No ad networks. No trackers sold to anyone.</li>
      </ul>
      <h2>Kids</h2>
      <p>There is no kid login and no kid-facing screen. Kid profile data is minimal,
        never used for ads, never sold, and deleted with your account.</p>
      <h2>Your control</h2>
      <p>Delete your account any time from <Link to="/pricing">Plans → Account</Link>:
        photos, kits, kid profiles, billing history — gone immediately, and any
        subscription is canceled. Stripe keeps receipts as financial law requires, but
        unlinked from you.</p>
      <p className="muted">Full engineering detail: docs/PRIVACY.md · Questions:
        privacy contact at launch domain (see docs).</p>
    </Chrome>
  );
}

export function TermsPage() {
  return (
    <Chrome title="Terms">
      <h2>The deal</h2>
      <p>VocabKit turns your photos of word lists into printable learning kits. The free
        plan includes exactly one complete kit — yours forever. Paid plans renew monthly
        or yearly until you cancel (anytime, from the billing page).</p>
      <h2>Your content</h2>
      <p>You own the lists you photograph. You let us use them to build your kits. Please
        only upload what you&apos;re entitled to use this way. We don&apos;t generate
        trademarked characters or copyrighted content — the capability simply doesn&apos;t
        exist in the product. Kit outputs are for personal family use.</p>
      <h2>Cancellations &amp; refunds</h2>
      <p>Cancel anytime. No partial-period refunds if you delete your account or cancel
        mid-period — you keep access for what you paid for until the period ends, unless
        deletion is your own choice, which ends it immediately. Exceptions at our
        discretion; just ask support.</p>
      <h2>Disclaimers</h2>
      <p>It&apos;s an educational tool; we can&apos;t guarantee learning outcomes. The
        service is provided &quot;as is&quot; to the extent the law allows, and our
        liability is capped at the fees you paid in the last 12 months.</p>
      <h2>Copyright (DMCA)</h2>
      <p>We design the product not to infringe — no scraping, no character generation —
        but if you believe content on VocabKit infringes your copyright, send us: what the
        work is, where our copy is, your contact info, and a good-faith statement. We
        acknowledge within 3 business days and act within 10. Counter-notices restore
        content in 10–14 business days absent a lawsuit. Two substantiated notices end the
        account. Contact: dmca contact at launch domain (see docs/TERMS.md).</p>
      <p className="muted">Full text: docs/TERMS.md · Operated by Wildium LLC.</p>
    </Chrome>
  );
}
